import { useEffect, useRef, useState } from 'react';
import { io, type Socket } from 'socket.io-client';
import { WS_EVENT, WS_NAMESPACE } from '@kick/types';
import { obtenerToken } from './auth-store';

const baseURL = import.meta.env.VITE_API_URL ?? 'http://localhost:3000';

export type EstadoConexion = 'conectando' | 'conectado' | 'desconectado';

/**
 * Conexión al canal del cliente.
 *
 * Solo se abre con sesión iniciada: la tienda es pública y un visitante sin
 * cuenta no tiene nada que escuchar. Una sola conexión por pestaña.
 */
let socket: Socket | null = null;
const suscriptores = new Set<(estado: EstadoConexion) => void>();
let estadoActual: EstadoConexion = 'desconectado';
/** Pedidos a los que se pidió sala: se vuelven a pedir al reconectar. */
const pedidosSuscritos = new Set<string>();

function avisar(estado: EstadoConexion): void {
  estadoActual = estado;
  suscriptores.forEach((s) => s(estado));
}

function conectar(): Socket | null {
  if (!obtenerToken()) return null;
  if (socket) return socket;

  socket = io(`${baseURL}${WS_NAMESPACE.CUSTOMER}`, {
    auth: (cb) => cb({ token: obtenerToken() }),
    transports: ['websocket', 'polling'],
    reconnection: true,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 10_000,
  });

  socket.on('connect', () => {
    avisar('conectado');
    // Las salas viven en el servidor y se pierden al caerse la conexión.
    pedidosSuscritos.forEach((id) => socket?.emit(WS_EVENT.SUBSCRIBE_ORDER, { orderId: id }));
  });
  socket.on('disconnect', () => avisar('desconectado'));
  socket.on('connect_error', () => avisar('desconectado'));
  socket.io.on('reconnect_attempt', () => avisar('conectando'));

  /**
   * El navegador sabe que se cayó la red antes que el socket.
   *
   * Sin esto habría que esperar al latido para enterarse, y el seguimiento
   * seguiría pareciendo al día mientras ya no llega nada.
   */
  window.addEventListener('offline', () => avisar('desconectado'));
  window.addEventListener('online', () => sincronizar());
  // Al volver a la pestaña (el móvil la suspende al cambiar de app), lo mismo.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') sincronizar();
  });

  return socket;
}

/**
 * Pone el estado mostrado de acuerdo con el socket real.
 *
 * Un corte breve puede no llegar a matar la conexión: el navegador avisa
 * `offline` y luego `online`, pero el socket nunca se enteró. Si en ese caso
 * se anunciara «reconectando», el estado se quedaría así para siempre —no
 * habrá ningún evento `connect` que lo saque— aunque el canal esté perfecto.
 *
 * Volver a `conectado` también dispara la reconciliación en las pantallas, que
 * es lo que hace falta: mientras duró el corte pudieron perderse eventos.
 */
function sincronizar(): void {
  if (!socket) return;

  if (socket.connected) {
    avisar('conectado');
    return;
  }

  avisar('conectando');
  // `active` es false solo cuando el socket ya no piensa reintentar por su
  // cuenta; forzar `connect()` en medio de su ciclo lo deja colgado.
  if (!socket.active) socket.connect();
}

export function cerrarSocket(): void {
  socket?.disconnect();
  socket = null;
  pedidosSuscritos.clear();
  avisar('desconectado');
}

/** Suscribe un manejador mientras el componente esté montado. */
export function useEventoSocket<T>(evento: string, manejador: (datos: T) => void): void {
  const referencia = useRef(manejador);
  referencia.current = manejador;

  useEffect(() => {
    const s = conectar();
    if (!s) return;

    const escuchar = (datos: T): void => referencia.current(datos);
    s.on(evento, escuchar);
    return () => {
      s.off(evento, escuchar);
    };
  }, [evento]);
}

/**
 * Entra a la sala de un pedido para recibir sus eventos.
 *
 * El servidor comprueba que el pedido sea de quien lo pide; aquí solo se
 * recuerda cuál era para volver a pedirlo tras una reconexión.
 */
export function useSalaPedido(orderId: string | undefined): void {
  useEffect(() => {
    if (!orderId) return;
    const s = conectar();
    if (!s) return;

    pedidosSuscritos.add(orderId);
    if (s.connected) s.emit(WS_EVENT.SUBSCRIBE_ORDER, { orderId });

    return () => {
      pedidosSuscritos.delete(orderId);
      s.emit(WS_EVENT.UNSUBSCRIBE_ORDER, { orderId });
    };
  }, [orderId]);
}

export function useConexion(): EstadoConexion {
  const [estado, setEstado] = useState<EstadoConexion>(estadoActual);

  useEffect(() => {
    const s = conectar();
    setEstado(s?.connected ? 'conectado' : s ? 'conectando' : 'desconectado');
    suscriptores.add(setEstado);
    return () => {
      suscriptores.delete(setEstado);
    };
  }, []);

  return estado;
}
